// Zod DTOs for the `/v1/me/notifications*` surface (TOPBAR-T4/T5,
// `docs/design/topbar-technical-approach.md` §7/§8) — single source of
// truth for `apps/api`'s validators + the OpenAPI generator, file-per-domain
// convention mirroring `./access-grant.ts`/`./command.ts`.
import { z } from 'zod';
import { NotificationType } from '../enums';
import { idSchema, paginatedResponseSchema, paginationQuerySchema } from './primitives';

const notificationTypeEnumSchema = z.enum(
  Object.values(NotificationType) as [NotificationType, ...NotificationType[]],
);

// ─── GET /v1/me/notifications ────────────────────────────────────────────

export const notificationListFilterSchema = z.enum(['all', 'unread']).default('all');

export const notificationListQuerySchema = paginationQuerySchema.extend({
  filter: notificationListFilterSchema,
});
export type NotificationListQuery = z.infer<typeof notificationListQuerySchema>;

/** Wire shape for one notification row — narrower than the Prisma row
 * (never leaks `userId`, same "a student never needs to see their own
 * foreign-key back-reference" posture `../../apps/api/src/services/
 * me-resource.ts`'s projections already follow). `payload` is re-parsed
 * from its TEXT-JSON storage form (ADR-002.1) into a plain JSON value on
 * the way out — never returned as a raw string. */
export const notificationSchema = z.object({
  id: idSchema,
  type: notificationTypeEnumSchema,
  title: z.string(),
  body: z.string(),
  linkUrl: z.string().nullable(),
  payload: z.unknown(),
  createdAt: z.string().datetime(),
  readAt: z.string().datetime().nullable(),
});
export type NotificationDto = z.infer<typeof notificationSchema>;

export const notificationListResponseSchema = paginatedResponseSchema(notificationSchema);
export type NotificationListResponse = z.infer<typeof notificationListResponseSchema>;

// ─── GET /v1/me/notifications/unread-count ───────────────────────────────

export const notificationUnreadCountResponseSchema = z.object({
  unread: z.number().int().nonnegative(),
});
export type NotificationUnreadCountResponse = z.infer<
  typeof notificationUnreadCountResponseSchema
>;
